import { Box, ListItem, ListItemText } from "@mui/material";
import { useState, useEffect } from "react";

import { EditFieldButton } from "@/shared/components/EditFieldButton";
import { ActionParameter } from "@/shared/domain/actions";

import { ActionParamsTable } from "./ActionParamsTable";
import { ClearFieldButton } from "./ClearFieldButton";

interface EditableActionParamsProps<UpdateType> {
  value: ActionParameter[];
  onChange: (updates: UpdateType) => void;
  paramValues?: {
    [key: string]: unknown;
  };
  setErrorText: (arg: string) => void;
  overrides?: boolean;
  canEdit?: boolean;
}

export default function EditableActionParams<UpdateType>({
  value,
  onChange,
  paramValues,
  setErrorText,
  overrides,
  canEdit = false,
}: EditableActionParamsProps<UpdateType>) {
  const [showEditableIcon, setShowEditableIcon] = useState(false);
  const [editable, setEditable] = useState(false);
  const [parameters, setParameters] = useState<ActionParameter[]>(value);
  const [removeParameters, setRemoveParameters] = useState<string[]>([]);
  const [parameterValues, setParameterValues] = useState<
    | {
        [key: string]: unknown;
      }
    | undefined
  >(paramValues);

  useEffect(() => {
    setParameters(value);
  }, [value]);

  useEffect(() => {
    setParameterValues(paramValues);
  }, [paramValues]);

  function updateRecord() {
    let updates;

    if (paramValues) {
      // for trigger update, only send param values
      updates = {
        parameter_values: parameterValues,
      };
    } else {
      // for action update, send full params
      updates = {
        parameter_changeset: {
          put_parameters: parameters,
          remove_parameters: removeParameters,
        },
      };
    }

    setErrorText("");
    onChange(updates as UpdateType);
  }

  return (
    <ListItem
      onMouseOver={() => {
        setShowEditableIcon(true);
      }}
      onMouseLeave={() => {
        setShowEditableIcon(false);
      }}
    >
      <ListItemText
        primaryTypographyProps={{
          component: "span",
        }}
        primary={
          <>
            <span>Parameters</span>

            <EditFieldButton
              editable={editable}
              disabled={!canEdit}
              showEditableIcon={showEditableIcon}
              onClick={() => {
                if (editable) {
                  updateRecord();
                }
                setEditable(!editable);
              }}
            />

            {overrides && (
              <ClearFieldButton
                disabled={!editable && !canEdit}
                showIcon={showEditableIcon}
                onClick={updateRecord}
              />
            )}

            <Box>
              <>
                <Box>
                  <ActionParamsTable
                    editable={editable}
                    mode={parameterValues ? "values" : "parameters"}
                    params={parameters}
                    setParams={setParameters}
                    removeParams={removeParameters}
                    setRemoveParams={setRemoveParameters}
                    paramValues={parameterValues}
                    setParamValues={setParameterValues}
                  />
                </Box>
              </>
            </Box>
          </>
        }
      />
    </ListItem>
  );
}
